import { useFormContext, useWatch } from "react-hook-form";
import styles from "../VolumeDesignForm/VolumeDesignForm.module.css";
import { AvailableVolumeTemplates } from "../../constants";
import BrandColorPicker from "../VolumeTemplateSelectionScreen/BrandColorPicker";
import FormSubSection from "../../CommonFields/FormSubSection";
import { VariableTextField } from "../../CommonFields/VariableTextField";

const CtaBar = () => (
    <div className={styles.mockCta}>
        <div className={styles.mockCtaText} />
        <div className={styles.mockCtaPrices}>
            <div className={styles.mockCtaPrice} />
            <div className={styles.mockCtaCompare} />
        </div>
    </div>
);

// Modern: price sits stacked directly under the title; the tag badge moves to the far right.
const VerticalModernMock = () => (
    <div className={styles.mockCard}>
        {[false, true].map((active, i) => (
            <div key={i} className={`${styles.vRow} ${active ? styles.vRowActive : ""}`}>
                {active && <span className={styles.floatBadge} />}
                <span className={styles.radioDot} />
                <span className={styles.imgBox} />
                <div className={styles.vRowStack}>
                    <div className={styles.lineBar} style={{ width: 18 }} />
                    <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                        <div className={styles.priceBar} style={{ height: 5 }} />
                        {active && <div className={styles.compareBar} />}
                    </div>
                </div>
                <div className={styles.vRowEnd}>
                    <div className={`${styles.tagChip} ${styles.tagChipFilled}`} />
                </div>
            </div>
        ))}
        <CtaBar />
    </div>
);

// Classic: title + tag are stacked (not inline like Basic), price stays on the far right.
const VerticalClassicMock = () => (
    <div className={styles.mockCard}>
        {[false, true].map((active, i) => (
            <div key={i} className={`${styles.vRow} ${active ? styles.vRowActive : ""}`}>
                {active && <span className={styles.floatBadge} />}
                <span className={styles.radioDot} />
                <span className={styles.imgBox} />
                <div className={styles.vRowStack}>
                    <div className={styles.lineBar} style={{ width: 18 }} />
                    <div className={styles.tagChip} />
                </div>
                <div className={styles.vRowEnd}>
                    <div className={styles.priceBar} />
                    {active && <div className={styles.compareBar} />}
                </div>
            </div>
        ))}
        <CtaBar />
    </div>
);

// Horizontal (Basic / Modern): tiers sit side-by-side as centered mini-cards.
// Basic = square corners, outline tag, flat top banner on the callout tier.
// Modern = rounded corners, filled tag, a rotated corner-ribbon on the callout tier, shadow on the selected one.
const HorizontalMock = ({ modern }: { modern: boolean }) => (
    <div className={styles.mockCard}>
        <div className={styles.hRow}>
            {[0, 1, 2].map((i) => (
                <div
                    key={i}
                    className={`${styles.hCard} ${modern ? styles.hCardModern : ""} ${
                        i === 2 ? styles.hCardActive : ""
                    }`}
                >
                    {i === 1 &&
                        (modern ? (
                            <span className={styles.hCardModernRibbon} />
                        ) : (
                            <span className={styles.hCardBasicBanner} />
                        ))}
                    <span className={styles.radioDot} />
                    <div className={styles.lineBar} style={{ width: 16, height: 3 }} />
                    <div
                        className={`${styles.tagChip} ${modern ? styles.tagChipFilled : ""}`}
                        style={{ width: 12, height: 5 }}
                    />
                    <div className={styles.priceBar} style={{ width: 12, height: 5 }} />
                    <div className={styles.compareBar} style={{ width: 10 }} />
                </div>
            ))}
        </div>
        <CtaBar />
    </div>
);

// Vertical Elegant: a polished take on the row list - gradient number badge instead of a plain
// radio, a colored accent strip on the selected row, and a "save" pill next to the price.
const VerticalElegantMock = () => (
    <div className={styles.mockCard}>
        {[false, true].map((active, i) => (
            <div key={i} className={`${styles.elegantMockRow} ${active ? styles.elegantMockRowActive : ""}`}>
                <span className={styles.elegantMockAccent} style={{ opacity: active ? 1 : 0 }} />
                <span className={styles.elegantMockBadge}>{i + 1}</span>
                <div className={styles.vRowStack} style={{ gap: 2 }}>
                    <div className={styles.lineBar} style={{ width: 16 }} />
                    <div className={styles.lineBar} style={{ width: 10, height: 3, opacity: 0.5 }} />
                </div>
                <div className={styles.vRowEnd}>
                    {active && <span className={styles.elegantMockPill} />}
                    <div className={styles.priceBar} />
                </div>
            </div>
        ))}
        <CtaBar />
    </div>
);

const TEMPLATE_OPTIONS = [
    { key: AvailableVolumeTemplates.VERTICAL_MODERN, label: "Vertical Modern", Mock: VerticalModernMock },
    { key: AvailableVolumeTemplates.VERTICAL_CLASSIC, label: "Vertical Classic", Mock: VerticalClassicMock },
    {
        key: AvailableVolumeTemplates.HORIZONTAL_MODERN,
        label: "Horizontal Modern",
        Mock: () => <HorizontalMock modern />,
    },
    {
        key: AvailableVolumeTemplates.HORIZONTAL_BASIC,
        label: "Horizontal Basic",
        Mock: () => <HorizontalMock modern={false} />,
    },
    { key: AvailableVolumeTemplates.VERTICAL_ELEGANT, label: "Vertical Elegant", Mock: VerticalElegantMock },
];

const ElegantBadgeVariables = [
    { content: "Number", value: "{number}" },
    { content: "Quantity", value: "{quantity}" },
    { content: "Discount %", value: "{discount_in_percent}" },
];

const VolumeDesignTemplateQuickSelect = () => {
    const { control, setValue } = useFormContext();
    const selectedTemplate = useWatch({ control, name: "selected_template_volume" });
    const isElegantTemplate = selectedTemplate === AvailableVolumeTemplates.VERTICAL_ELEGANT;

    const handleSelect = (template: string) => {
        if (template === selectedTemplate) return;
        setValue("selected_template_volume", template, { shouldDirty: true });
        setValue("show_variant_selector", !template.startsWith("horizontal"), { shouldDirty: true });
    };

    return (
        <div className={styles.wrapper}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 5 }}>
                {/* <div className={styles.title}>Template</div> */}
                <BrandColorPicker />
            </div>
            <div className={styles.optionsRow}>
                {TEMPLATE_OPTIONS.map(({ key, label, Mock }) => (
                    <div
                        key={key}
                        className={`${styles.option} ${selectedTemplate === key ? styles.optionActive : ""}`}
                        onClick={() => handleSelect(key)}
                    >
                        <Mock />
                        <div className={styles.optionLabel}>{label}</div>
                    </div>
                ))}
            </div>

            {isElegantTemplate && (
                <div id="product-card-elegant-badge" style={{ marginTop: 16 }}>
                    <FormSubSection rowLabel={"Tier Badge"} hideSeparator>
                        <VariableTextField
                            name="bundles_design.elegant_badge_content"
                            label="Badge Content"
                            placeholder="{number}"
                            maxLength={30}
                            showCharacterCount
                            tooltipContent="Leave blank to hide the badge. Combine variables with your own text, e.g. #{number} or {quantity}x."
                            variables={ElegantBadgeVariables}
                        />
                    </FormSubSection>
                </div>
            )}
        </div>
    );
};

export default VolumeDesignTemplateQuickSelect;
